import { useState } from "react";
import { Loader2, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { CATEGORY_LABELS } from "@/lib/components";
import { TestPriceBox, convert, problems } from "@/components/settings/LineTools";
import { Segmented } from "@/components/ui/segmented";

export const EMPTY_LINE = {
  name: "", kind: "frame_sash", type_id: null, frame_rate: 0, sash_std_rate: 0, sash_over_rate: 0, std_sash_area: 13.73, sash_max_w: 0, sash_max_h: 0,
  min_area: 10.76, crate_rate: 0, base_price: 0, min_price: 0, default_layout: "X", crate_min_area: null, sash_min_area: null,
  glass_tiers: [], glass_upgrades: [], extras: [], tracks: [], notes: "", min_area_double: 0, valid_min: 0, valid_max: 0, pull: "", applies_to: [],
};
const NUMS = ["frame_rate", "sash_std_rate", "sash_over_rate", "std_sash_area", "sash_max_w", "sash_max_h", "min_area", "crate_rate", "base_price", "min_price", "min_area_double", "valid_min", "valid_max"];
const OPT = [["crate_min_area", "Crate min area (blank = same)"], ["sash_min_area", "Sash min area (blank = same)"]];
const SCREEN_FIELDS = [["frame_rate", "Screen $/sq ft"], ["min_area", "Min area single-pull (sq ft)"], ["min_area_double", "Min area double-pull (sq ft, 0 = none)"],
  ["valid_min", "Valid range from (sq ft, 0 = none)"], ["valid_max", "Valid range to (sq ft, 0 = none)"]];
const SCREEN_CATS = Object.entries(CATEGORY_LABELS).filter(([k]) => k !== "screen" && k !== "fixed");

function ScreenFields({ f, set }) {
  const on = (c) => (f.applies_to || []).includes(c);
  const flip = (c) => set("applies_to", on(c) ? f.applies_to.filter((x) => x !== c) : [...(f.applies_to || []), c]);
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="line-editor-screen-fields">
    {SCREEN_FIELDS.map(([k, label]) => (
        <label key={k} className="text-xs text-slate-400">{label}
          <input data-testid={`line-editor-${k}`} inputMode="decimal" value={fmt(f[k])} onChange={(e) => set(k, e.target.value)} className={`${inputCls} text-right font-mono-num`} /></label>
      ))}
      <label className="text-xs text-slate-400">Pull
        <select data-testid="line-editor-pull" value={f.pull || "single"} onChange={(e) => set("pull", e.target.value)} className={inputCls}>
          <option value="single">Single-pull</option><option value="double">Double-pull</option><option value="both">Single or double (picked per line)</option>
        </select></label>
      <div className="sm:col-span-3" data-testid="line-editor-applies-to">
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Applies to</p>
        <div className="flex flex-wrap gap-3">
          {SCREEN_CATS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-1.5 text-xs text-slate-300">
              <input type="checkbox" data-testid={`line-editor-applies-${k}`} checked={on(k)} onChange={() => flip(k)} /> {l}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}
const FIELDS = {
  frame_sash: [["frame_rate", "Frame $/sq ft"], ["sash_std_rate", "Sash std $/sq ft"], ["sash_over_rate", "Sash oversized $/sq ft"],
    ["sash_max_w", "Std sash max width (in, 0 = guide)"], ["sash_max_h", "Std sash max height (in, 0 = guide)"], ["min_area", "Min billable area (sq ft)"], ["crate_rate", "Crate $/sq ft"],
    ["base_price", "Base price $"], ["min_price", "Min unit price $"]],
  slider: [["min_area", "Min billable area (sq ft)"], ["crate_rate", "Crate $/sq ft"], ["base_price", "Base price $"], ["min_price", "Min unit price $"]],
};
const LISTS = [
  ["glass_tiers", "Glass surcharge per pane (by pane area)", [["from_sqft", "Over sq ft"], ["to_sqft", "Up to sq ft"], ["rate", "$/sq ft"]]],
  ["glass_upgrades", "Glass upgrades", [["name", "Name"], ["rate", "$/sq ft"]]],
  ["extras", "Extras", [["name", "Name"], ["unit", "Unit"], ["rate", "$"], ["default_on", "On by default"]]],
  ["tracks", "Track rates (sliders)", [["name", "Track"], ["rate", "$/sq ft"]]],
];
const UNITS = [["piece", "per piece"], ["sash", "per opening sash"], ["panel", "per panel"], ["sqft", "per sq ft"], ["lin_ft", "per ft of width"]];
const fmt = (v) => (typeof v === "number" ? String(Math.round(v * 10000) / 10000) : v ?? "");
const cell = inputCls.replace("w-full", "");

function RowsEditor({ k, title, cols, rows, onChange }) {
  const blank = Object.fromEntries(cols.map(([c]) => [c, c === "unit" ? "piece" : c === "name" ? "" : c === "default_on" ? false : 0]));
  const set = (i, c, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [c]: v } : r)));
  return (
    <div data-testid={`line-editor-${k}`}>
      <div className="mb-1 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
        {title}
        <button type="button" data-testid={`line-editor-${k}-add`} onClick={() => onChange([...rows, blank])} className="inline-flex items-center gap-1 text-amber-300 hover:text-amber-200"><Plus className="h-3 w-3" /> Add</button>
      </div>
      {rows.length > 0 && (
        <div className="mb-1 flex gap-1 text-[10px] text-slate-500">
          {cols.map(([c, label]) => <span key={c} className={c === "name" ? "flex-1" : c === "unit" ? "w-32" : "w-28 text-right"}>{label}</span>)}
          <span className="w-6" />
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="mb-1 flex gap-1">
          {cols.map(([c, label]) => c === "default_on" ? (
            <label key={c} className="flex w-28 shrink-0 items-center justify-center gap-1 text-[10px] text-slate-400">
              <input type="checkbox" data-testid={`line-editor-${k}-default-${i}`} checked={!!r.default_on} onChange={(e) => set(i, c, e.target.checked)} /> on</label>
          ) : c === "unit" ? (
            <select key={c} aria-label={label} value={r.unit} onChange={(e) => set(i, c, e.target.value)} className={`${cell} w-32 shrink-0`}>
              {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          ) : (
            <input key={c} aria-label={label} placeholder={label} value={fmt(r[c])} onChange={(e) => set(i, c, e.target.value)}
              className={`${cell} ${c === "name" ? "min-w-0 flex-1" : "w-28 shrink-0 text-right font-mono-num"}`} />
          ))}
          <button type="button" aria-label="Remove" data-testid={`line-editor-${k}-remove-${i}`} onClick={() => onChange(rows.filter((_, j) => j !== i))} className="px-1 text-slate-500 hover:text-red-300"><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

// m² edits are converted back to sq ft; values the Admin didn't touch keep their exact stored value (no rounding drift).
const keepExact = (back, orig) => {
  if (!orig) return back;
  const out = { ...back };
  const there = convert(orig, 1), round = convert(there, -1);
  Object.keys(back).forEach((k) => {
    if (Array.isArray(back[k])) {
      out[k] = back[k].map((r, i) => (JSON.stringify(r) === JSON.stringify(round[k]?.[i]) ? orig[k][i] : r));
    } else if (typeof orig[k] === "number" && back[k] === round[k]) out[k] = orig[k];
  });
  return out;
};

const toBody = (f0, m2, orig) => {
  const f = m2 ? keepExact(convert(f0, -1), orig) : f0;
  const num = (v) => Number(v) || 0;
  const opt = (v) => (v === "" || v == null ? null : Number(v));
  const list = (rows, numCols) => rows.map((r) => ({ ...r, ...Object.fromEntries(numCols.map((c) => [c, num(r[c])])) }));
  return {
    ...f, ...Object.fromEntries(NUMS.map((k) => [k, num(f[k])])), type_id: f.type_id || null,
    crate_min_area: opt(f.crate_min_area), sash_min_area: opt(f.sash_min_area),
    glass_tiers: list(f.glass_tiers, ["from_sqft", "to_sqft", "rate"]), glass_upgrades: list(f.glass_upgrades, ["rate"]),
    extras: list(f.extras, ["rate"]), tracks: list(f.tracks, ["rate"]),
  };
};

export function LineEditor({ line, types, onClose, onSaved }) {
  const base = { ...EMPTY_LINE, ...line };
  const [f, setF] = useState(base);
  const [m2, setM2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errs, setErrs] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const flip = () => { setF((s) => (m2 ? keepExact(convert(s, -1), base) : convert(s, 1))); setM2(!m2); };
  const save = async () => {
    const bad = problems(m2 ? convert(f, -1) : f);
    setErrs(bad);
    if (bad.length) return;
    setBusy(true);
    try {
      const body = toBody(f, m2, base);
      if (line.id) await api.put(`/settings/product-lines/${line.id}`, body);
      else await api.post("/settings/product-lines", body);
      toast.success("Product line saved");
      onSaved();
      onClose();
    } catch (e) {
      setErrs([errorMessage(e)]);
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const U = m2 ? "m²" : "sq ft";
  const lab = (s) => s.replace(/sq ft/g, U);
  return (
    <Dialog open onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="line-editor-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto bg-slate-950">
        <DialogHeader><DialogTitle className="flex items-center justify-between gap-3 pr-6 font-display text-slate-50">{line.id ? "Edit product line" : "New product line"}
          <Segmented testId="line-editor-unit-toggle" value={m2 ? "m2" : "sqft"} options={[["sqft", "$/sq ft"], ["m2", "$/m²"]]} onChange={flip} /></DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs text-slate-400 sm:col-span-1">Name
            <input data-testid="line-editor-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></label>
          <label className="text-xs text-slate-400">Category
            <select data-testid="line-editor-category" value={f.category || ""} onChange={(e) => set("category", e.target.value)} className={inputCls}>
              <option value="">Detect from name</option>
              {Object.entries(CATEGORY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select></label>
          {f.category !== "screen" && (<>
          <label className="text-xs text-slate-400">Kind
            <select data-testid="line-editor-kind" value={f.kind} onChange={(e) => set("kind", e.target.value)} className={inputCls}>
              <option value="frame_sash">Frame + sash (casement, awning, hung, doors)</option>
              <option value="slider">Sliding (track rate)</option>
            </select></label>
          <label className="text-xs text-slate-400">Type
            <select data-testid="line-editor-type" value={f.type_id || ""} onChange={(e) => set("type_id", e.target.value || null)} className={inputCls}>
              <option value="">Not mapped</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select></label>
          {FIELDS[f.kind].map(([k, label]) => (
            <label key={k} className="text-xs text-slate-400">{lab(label)}
              <input data-testid={`line-editor-${k}`} inputMode="decimal" value={fmt(f[k])} onChange={(e) => set(k, e.target.value)} className={`${inputCls} text-right font-mono-num`} /></label>
          ))}
          {OPT.map(([k, label]) => (
            <label key={k} className="text-xs text-slate-400">{label} ({U})
              <input data-testid={`line-editor-${k}`} inputMode="decimal" value={fmt(f[k])} placeholder="same" onChange={(e) => set(k, e.target.value)} className={`${inputCls} text-right font-mono-num`} /></label>
          ))}
          <label className="text-xs text-slate-400">Default panel layout
            <input data-testid="line-editor-default-layout" value={f.default_layout} onChange={(e) => set("default_layout", e.target.value)} placeholder={f.kind === "slider" ? "XO" : "O|X"} className={`${inputCls} font-mono`} /></label>
          </>)}
        </div>
        {f.category === "screen" ? <ScreenFields f={f} set={set} /> : (
        <div className="mt-2 grid gap-4">
          {LISTS.filter(([k]) => k !== "tracks" || f.kind === "slider").map(([k, title, cols]) => (
            <RowsEditor key={k} k={k} title={lab(title)} cols={cols.map(([c, l]) => [c, lab(l)])} rows={f[k]} onChange={(v) => set(k, v)} />
          ))}
        </div>
        )}
        <label className="text-xs text-slate-400">Notes
          <textarea data-testid="line-editor-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} rows={2} className={`${inputCls} h-auto py-1`} /></label>
        {f.category !== "screen" && <TestPriceBox body={() => toBody(f, m2, base)} />}
        {errs.length > 0 && (
          <ul data-testid="line-editor-errors" className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {errs.map((e) => <li key={e}>{e}</li>)}
          </ul>
        )}
        <DialogFooter>
          <button data-testid="line-editor-cancel" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-300 hover:text-slate-50">Cancel</button>
          <button data-testid="line-editor-save" onClick={save} disabled={busy || !f.name.trim()}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 transition-colors hover:bg-amber-400 disabled:opacity-40">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
